import React from 'react'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import favicon from '../../img/favicon.png'
import smartsaleFavicon from '../../img/smartsale-favicon.png'
import { useApplicationTheme, ProductLine } from '@adc/ui-core'
import { getAccessibilityScript } from '../auctionApplicationProvider/getAccessiblityScript'
import { getOneTrustScript } from '../auctionApplicationProvider/getOneTrustScript'
import { getOptimizelyScript } from '../auctionApplicationProvider/getOptimizelyScript'

const defaultTitleTemplate = '%s'
const defaultAuctioncomHelmet = {
  title: 'Auction.com',
  titleTemplate: defaultTitleTemplate,
  canonicalHref: 'https://auction.com',
  description: '',
  children: null,
}
const defaultSmartsaleHelmet = {
  title: 'SmartSale',
  titleTemplate: defaultTitleTemplate,
  canonicalHref: 'https://smartsale.com',
  description: '',
  children: null,
}

// TODO: Page title should allow for updating elsewhere. Should be a context key.
const SharedPageMetadata = ({
  helmetConfig = {},
  sspConfig = {},
  featureFlags,
  accessibilityOptions = {},
}) => {
  const theme = useApplicationTheme()

  const isAuctioncom = theme.productLine === ProductLine.AuctionCom

  // Determine the default helmet configuration based on the theme.productLine
  const defaultHelmetConfig =
    theme.productLine === ProductLine.SmartSale
      ? defaultSmartsaleHelmet
      : defaultAuctioncomHelmet

  // Overlay the passed-in helmetConfig on top of the default configuration
  const mergedHelmetConfig = {
    ...defaultHelmetConfig,
    ...helmetConfig,
  }

  const appleAppArgument =
    typeof window === 'undefined' ? '' : `app-argument=${window.location}`

  const googlePlaystoreId = sspConfig?.appstore?.adc?.GOOGLE_PLAY_ID || null
  const appleItunesId = sspConfig?.appstore?.adc?.APPLE_ITUNES_ID || null

  const googlePlayApp = {
    name: 'google-play-app',
    content: `app-id=${googlePlaystoreId}&referrer=utm_source%3Dsmartapp`,
  }

  const appleItunesApp = {
    name: 'apple-itunes-app',
    content: [
      `app-id=${appleItunesId}`,
      'affiliate-data=ct=SmartAppBanner&pt=315353',
      appleAppArgument,
    ].join(' '),
  }

  const metaContent = [
    {
      name: 'description',
      id: 'description',
      content: mergedHelmetConfig.description,
    },
    { name: 'keywords', content: '' },
    ...(isEmpty(googlePlaystoreId) && !isAuctioncom ? [] : [googlePlayApp]),
    ...(isEmpty(appleItunesId) && !isAuctioncom ? [] : [appleItunesApp]),
  ]

  const _favicon =
    theme.productLine === ProductLine.SmartSale ? smartsaleFavicon : favicon
  return (
    <Helmet
      title={mergedHelmetConfig.title}
      titleTemplate={mergedHelmetConfig.titleTemplate}
      link={[{ rel: 'icon', href: _favicon }]}
      meta={metaContent}
    >
      <script
        id='adc-injection-onetrust'
        innerHTML={getOneTrustScript()}
        type='module' // this will treat the inline similar to src and defer it
        defer // important - this script has to come after document loads
      />

      {accessibilityOptions?.equalweb?.site_key &&
        accessibilityOptions?.equalweb?.enabled && (
          <script
            id='adc-injection'
            innerHTML={getAccessibilityScript(accessibilityOptions?.equalweb)}
            type='module' // this will treat the inline similar to src and defer it
            defer // important - this script has to come after document loads
          />
        )}

      {featureFlags['fe.resi.enable-optimizely-script'] && (
        <script
          id='adc-injection-optimizely'
          innerHTML={getOptimizelyScript()}
          type='module' // this will treat the inline similar to src and defer it
          defer // important - this script has to come after document loads
        />
      )}

      {mergedHelmetConfig.children}
    </Helmet>
  )
}

SharedPageMetadata.propTypes = {
  helmetConfig: PropTypes.shape({
    title: PropTypes.string,
    titleTemplate: PropTypes.string,
    canonicalHref: PropTypes.string,
    description: PropTypes.string,
    children: PropTypes.node,
  }),
  sspConfig: PropTypes.object,
  featureFlags: PropTypes.object,
  accessibilityOptions: PropTypes.shape({
    equalweb: PropTypes.object,
  }),
}

export default SharedPageMetadata
